import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, MapPin, Building2, Package, ShieldCheck } from "lucide-react";
import { CASE_STUDIES, SECTORS } from "@/data/hvm";
import { track } from "@/utils/analytics";

const MetaItem = ({ icon: Icon, label, value }) => (
  <div className="flex gap-3">
    <Icon size={15} className="text-[#F4A031] shrink-0 mt-0.5" />
    <div>
      <p className="font-tech text-[10px] tracking-[0.25em] uppercase text-[#64748B]">{label}</p>
      <p className="text-sm text-white mt-0.5">{value}</p>
    </div>
  </div>
);

export const Proof = () => {
  const [openId, setOpenId] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const active = CASE_STUDIES.find((c) => c.id === openId);

  const openCase = (id) => {
    track("case_study_opened", id);
    setOpenId(id);
    setImgIdx(0);
  };

  return (
    <section id="proof" data-testid="proof-section" className="relative py-24 sm:py-32 bg-[#0E1420] border-y border-[#26334D] noise-overlay">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-tech text-[11px] tracking-[0.3em] uppercase text-[#F4A031]"
        >
          Proof in the ground
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight mt-4"
        >
          Specified where<br /><span className="text-[#F4A031]">failure isn't</span> an option.
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.18 }}
          className="text-[#94A3B8] mt-5 max-w-2xl"
        >
          Real Access X projects across New Zealand — tap any project for the full brief:
          client, site constraints, products supplied and certified rating.
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {CASE_STUDIES.map((c, i) => (
            <motion.article
              key={c.id}
              data-testid={`case-study-${i}`}
              role="button"
              tabIndex={0}
              onClick={() => openCase(c.id)}
              onKeyDown={(e) => e.key === "Enter" && openCase(c.id)}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: (i % 3) * 0.1 }}
              className="group relative clip-notch overflow-hidden border border-[#26334D] hover:border-[#F4A031]/60 transition-colors cursor-pointer text-left"
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={c.image}
                  alt={`${c.title} — hostile vehicle mitigation installation, ${c.tag}`}
                  loading="lazy"
                  width="1400"
                  height="1050"
                  className="w-full h-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-transparent to-transparent" />
                <span className="absolute top-4 left-4 font-tech text-[10px] tracking-[0.2em] uppercase text-[#0B0F17] bg-[#F4A031] px-2.5 py-1">
                  {c.tag}
                </span>
              </div>
              <div className="p-6 bg-[#131927]">
                <p className="font-tech text-xs text-[#F4A031]">{c.stat}</p>
                <h3 className="font-grotesk font-bold text-lg text-white mt-1.5">{c.title}</h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed mt-2">{c.detail}</p>
                <p className="inline-flex items-center gap-2 font-tech text-[11px] tracking-widest uppercase text-[#F4A031] mt-4 group-hover:gap-3 transition-all">
                  View case study <ArrowRight size={13} />
                </p>
              </div>
            </motion.article>
          ))}
        </div>

        <motion.div
          data-testid="sectors-strip"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-12 flex flex-wrap items-center gap-x-3 gap-y-3"
        >
          <span className="font-tech text-[10px] tracking-[0.3em] uppercase text-[#64748B] mr-2">Sectors served</span>
          {SECTORS.map((s) => (
            <span
              key={s}
              className="font-tech text-[11px] tracking-widest uppercase text-[#94A3B8] border border-[#26334D] px-3 py-1.5 clip-notch-sm"
            >
              {s}
            </span>
          ))}
        </motion.div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="case-study-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-8"
          >
            <div
              data-testid="case-study-modal-backdrop"
              className="absolute inset-0 bg-[#0B0F17]/85 backdrop-blur-sm"
              onClick={() => setOpenId(null)}
            />
            <motion.div
              initial={{ opacity: 0, y: 32, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="relative bg-[#131927] border border-[#26334D] clip-notch w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            >
              <button
                data-testid="case-study-modal-close"
                onClick={() => setOpenId(null)}
                aria-label="Close case study"
                className="absolute top-4 right-4 z-10 bg-[#0B0F17]/80 border border-[#384B70] hover:border-[#F4A031] text-white p-2.5 clip-notch-sm transition-colors"
              >
                <X size={18} />
              </button>

              <div className="relative h-64 sm:h-96">
                <img
                  src={active.images[imgIdx]}
                  alt={`${active.title} — ${active.products}, ${active.location}`}
                  data-testid="case-study-modal-image"
                  width="1400"
                  height="1050"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#131927] via-transparent to-transparent" />
                <span className="absolute top-4 left-4 font-tech text-[10px] tracking-[0.2em] uppercase text-[#0B0F17] bg-[#F4A031] px-2.5 py-1">
                  {active.tag}
                </span>
              </div>

              {active.images.length > 1 && (
                <div className="flex gap-3 px-6 sm:px-9 pt-5">
                  {active.images.map((src, i) => (
                    <button
                      key={src}
                      data-testid={`case-study-thumb-${i}`}
                      onClick={() => setImgIdx(i)}
                      className={`relative h-16 w-24 overflow-hidden clip-notch-sm border transition-colors ${
                        imgIdx === i ? "border-[#F4A031]" : "border-[#26334D] hover:border-[#384B70]"
                      }`}
                    >
                      <img src={src} alt={`${active.title} — photo ${i + 1} of ${active.images.length}`} loading="lazy" width="96" height="64" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}

              <div className="p-6 sm:p-9">
                <p className="font-tech text-xs text-[#F4A031]">{active.stat}</p>
                <h3 className="font-display font-bold uppercase text-2xl sm:text-3xl tracking-tight text-white mt-2">
                  {active.title}
                </h3>
                <p className="text-[#94A3B8] leading-relaxed mt-4">{active.body}</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-8 pt-7 border-t border-[#26334D]">
                  <MetaItem icon={Building2} label="Client" value={active.client} />
                  <MetaItem icon={MapPin} label="Location" value={active.location} />
                  <MetaItem icon={Package} label="Products supplied" value={active.products} />
                  <MetaItem icon={ShieldCheck} label="Certified rating" value={active.rating} />
                </div>

                <a
                  href="#assessment"
                  data-testid="case-study-modal-cta"
                  onClick={() => setOpenId(null)}
                  className="mt-8 inline-flex items-center gap-3 bg-[#F4A031] hover:bg-[#D98A1F] text-[#0B0F17] font-grotesk font-bold px-6 py-3.5 clip-notch-sm transition-colors"
                >
                  Scope a project like this <ArrowRight size={16} />
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
